/**
 * Raster assets under `public/illustrations/flow/` — see `public/illustrations/flow/README.txt`.
 *
 * MISSING ASSETS — org-step-01..08.png have not been created yet.
 * Until they are added, onboarding steps temporarily reuse the existing client-step artwork.
 * Drop in `org-step-01.png` … `org-step-07.png` (wide ~4:3 or 16:10, transparent/soft bg) to
 * replace the standin images automatically — no code change needed.
 *
 * Step labels for designers:
 *   org-step-01.png → Welcome / workspace creation
 *   org-step-02.png → Organisation identity / branding
 *   org-step-03.png → Policies & consent
 *   org-step-04.png → Email / SMTP deliverability
 *   org-step-05.png → Team / invite collaborators
 *   org-step-06.png → Pipeline / first client
 *   org-step-07.png → Setup checklist / ready to hire
 */

/**
 * Onboarding cover images — 7 UI steps.
 * Uses dedicated org-step artwork when available; falls back to client-step standin files.
 * `FlowCoverImage` will further fall back to an inline SVG if neither exists.
 */
/**
 * Org-step artwork mapping — uses existing client-step PNGs until dedicated
 * org-step-01..07.png assets are added to public/illustrations/flow/.
 */
const ORG_STEP_TO_CLIENT_STEP: Record<number, number> = {
  1: 1, // Welcome            → client profile card
  2: 1, // Organisation       → client profile card
  3: 2, // Policies           → documents / shield
  4: 3, // Email delivery     → engagement / comms
  5: 3, // Team invite        → engagement / people
  6: 4, // Pipeline           → commercial overview
  7: 5, // Checklist          → review / complete
};

export function orgOnboardingCoverImage(uiStep1To7: number): string {
  const s = Math.min(7, Math.max(1, Math.floor(uiStep1To7)));
  // Primary: dedicated org-step artwork (drop org-step-01..07.png to activate)
  // Fallback: existing client-step artwork already in the repo
  const clientN = ORG_STEP_TO_CLIENT_STEP[s] ?? 1;
  const clientFallback = `/illustrations/flow/client-step-${String(clientN).padStart(2, '0')}.png`;
  return clientFallback;
}

/** Raw file index 1–8 (legacy / generic — kept for any callers outside onboarding). */
export function orgFlowImage(step1To8: number): string {
  const n = Math.min(8, Math.max(1, Math.floor(step1To8)));
  return `/illustrations/flow/org-step-${String(n).padStart(2, '0')}.png`;
}

/** `/clients/new` — 6 UI steps mapped onto existing client-step-01..04 artwork. */
const CLIENT_FLOW_COVER_INDEX = [1, 2, 3, 3, 4, 4] as const;

export function clientFlowImage(step1To6: number): string {
  const s = Math.min(6, Math.max(1, Math.floor(step1To6)));
  const n = CLIENT_FLOW_COVER_INDEX[s - 1];
  return `/illustrations/flow/client-step-${String(n).padStart(2, '0')}.png`;
}

/** Consultancy **tenant** offboarding (`/settings/offboarding`) — 6 UI steps; raster filenames documented in `public/illustrations/flow/README.txt`. */
export function offboardingCoverImage(step1To6: number): string {
  const n = Math.min(6, Math.max(1, Math.floor(step1To6)));
  return `/illustrations/flow/offboard-step-${String(n).padStart(2, '0')}.png`;
}
